// First-Touch-Attribution.
//
// Regeln:
// - Die Werte werden beim ersten Seitenaufruf technisch erfasst, aber NUR
//   flüchtig im Speicher gehalten, solange keine Einwilligung vorliegt.
// - Dauerhaft gespeichert (localStorage) wird erst mit Analytics-Einwilligung.
// - `fbclid` wird ausschließlich mit Marketing-Einwilligung gespeichert oder
//   weitergegeben.
// - Der erste erfasste Touch gewinnt (spätere Besuche überschreiben nicht).

import { loadConsent } from '@/lib/analytics';

export const FIRST_TOUCH_STORAGE_KEY = 'aimup_first_touch_v1';

export type FirstTouch = {
  first_landing_path?: string;
  referrer?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  fbclid?: string;
  captured_at?: string;
};

export type FirstTouchPayload = FirstTouch & {
  analytics_consent: boolean;
  marketing_consent: boolean;
};

const clip = (v: string | null | undefined, max: number): string | undefined => {
  if (!v) return undefined;
  const t = v.trim();
  if (!t) return undefined;
  return t.slice(0, max);
};

const readStored = (): FirstTouch | null => {
  try {
    const raw = localStorage.getItem(FIRST_TOUCH_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as FirstTouch) : null;
  } catch {
    return null;
  }
};

/** Referrer ohne Querystring/Hash: nur origin + pathname, sonst leer. */
const cleanReferrer = (raw: string | null | undefined): string | undefined => {
  const t = raw?.trim();
  if (!t) return undefined;
  try {
    const u = new URL(t);
    return clip(u.origin + u.pathname, 500);
  } catch {
    return undefined;
  }
};

const capture = (): FirstTouch => {

  if (typeof window === 'undefined') return {};
  try {
    const params = new URLSearchParams(window.location.search);
    const get = (k: string) => clip(params.get(k), 200);
    return {
      // Bewusst OHNE Querystring/Hash – sonst könnte z. B. fbclid indirekt
      // in Landing/Referrer landen, obwohl keine Marketing-Einwilligung vorliegt.
      first_landing_path: clip(window.location.pathname, 500),
      referrer: cleanReferrer(document.referrer),

      utm_source: get('utm_source'),
      utm_medium: get('utm_medium'),
      utm_campaign: get('utm_campaign'),
      utm_content: get('utm_content'),
      utm_term: get('utm_term'),
      fbclid: clip(params.get('fbclid'), 400),
      captured_at: new Date().toISOString(),
    };
  } catch {
    return {};
  }
};

// Flüchtiger Speicher – existiert immer, unabhängig von der Einwilligung.
let memoryTouch: FirstTouch = {};

const init = () => {
  if (typeof window === 'undefined') return;
  const stored = readStored();
  memoryTouch = stored ?? capture();
};

init();

/** Speichert den First Touch dauerhaft, sofern die Einwilligung das erlaubt. */
export const persistFirstTouchIfAllowed = () => {
  if (typeof window === 'undefined') return;
  try {
    const consent = loadConsent();
    if (!consent?.analytics) return;
    const existing = readStored();
    const base = existing ?? memoryTouch;
    const next: FirstTouch = { ...base };
    if (!consent.marketing) delete next.fbclid;
    localStorage.setItem(FIRST_TOUCH_STORAGE_KEY, JSON.stringify(next));
    memoryTouch = next;
  } catch {
    /* Tracking darf nie blockieren */
  }
};

/**
 * Liefert die zulässigen First-Touch-Werte plus die aktuellen
 * Einwilligungs-Flags. Ohne Analytics-Einwilligung werden keine
 * Herkunftsdaten weitergegeben.
 */
export const getFirstTouchCapturedAt = (): string | undefined => {
  const source = readStored() ?? memoryTouch;
  return source?.captured_at;
};

export const getFirstTouchPayload = (): FirstTouchPayload => {
  const consent = loadConsent();
  const analytics = !!consent?.analytics;
  const marketing = !!consent?.marketing;
  if (!analytics) {
    return { analytics_consent: false, marketing_consent: marketing };
  }
  const source = readStored() ?? memoryTouch;
  const payload: FirstTouchPayload = {
    ...source,
    analytics_consent: true,
    marketing_consent: marketing,
  };
  if (!marketing) delete payload.fbclid;
  delete (payload as FirstTouch).captured_at;
  return payload;
};

if (typeof window !== 'undefined') {
  // Sobald eine Einwilligung erteilt wurde, darf der First Touch bleiben.
  persistFirstTouchIfAllowed();
  window.addEventListener('aimup-cookie-consent-changed', () => {
    persistFirstTouchIfAllowed();
  });
}
